import React, { useState, useEffect } from 'react';
import { staffAPI } from '../services/api';
import ErrorBoundary from '../components/ErrorBoundary';
import { todayLocal } from '../utils/localDate';

const Staff = ({ selectedBusiness }) => {
  const [staff, setStaff] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [filters, setFilters] = useState({
    employment_status: '',
    employment_type: '',
    department: ''
  });

  useEffect(() => {
    loadData();
  }, [selectedBusiness, filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {
        ...(selectedBusiness && { business_id: selectedBusiness }),
        ...filters
      };
      
      const [staffRes, statsRes] = await Promise.all([
        staffAPI.getAll(params),
        staffAPI.getSummary(selectedBusiness ? { business_id: selectedBusiness } : {})
      ]);
      
      setStaff(staffRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load staff data:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert('Failed to load staff data');
    } finally {
      setLoading(false);
    }
  };

  const [formData, setFormData] = useState({
    business_id: '',
    employee_id: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    ssn_last_4: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'USA',
    hire_date: todayLocal(),
    termination_date: '',
    employment_status: 'active',
    employment_type: 'full_time',
    job_title: '',
    department: '',
    reports_to: '',
    pay_type: 'hourly',
    pay_rate: '',
    pay_frequency: 'biweekly',
    overtime_eligible: true,
    overtime_rate: '1.5',
    health_insurance: false,
    dental_insurance: false,
    vision_insurance: false,
    retirement_401k: false,
    retirement_contribution_percent: '',
    pto_hours_per_year: '',
    sick_hours_per_year: '',
    tax_filing_status: '',
    federal_withholding_allowances: '0',
    state_withholding_allowances: '0',
    additional_withholding: '0',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',
    notes: '',
    tags: [],
    custom_fields: null,
    is_active: true
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStaff) {
        await staffAPI.update(editingStaff.staff_id, formData);
      } else {
        await staffAPI.create({
          ...formData,
          business_id: selectedBusiness || formData.business_id
        });
      }
      setShowModal(false);
      setEditingStaff(null);
      setFormData({
        business_id: '',
        employee_id: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        date_of_birth: '',
        ssn_last_4: '',
        street_address: '',
        city: '',
        state: '',
        zip_code: '',
        country: 'USA',
        hire_date: todayLocal(),
        termination_date: '',
        employment_status: 'active',
        employment_type: 'full_time',
        job_title: '',
        department: '',
        reports_to: '',
        pay_type: 'hourly',
        pay_rate: '',
        pay_frequency: 'biweekly',
        overtime_eligible: true,
        overtime_rate: '1.5',
        health_insurance: false,
        dental_insurance: false,
        vision_insurance: false,
        retirement_401k: false,
        retirement_contribution_percent: '',
        pto_hours_per_year: '',
        sick_hours_per_year: '',
        tax_filing_status: '',
        federal_withholding_allowances: '0',
        state_withholding_allowances: '0',
        additional_withholding: '0',
        emergency_contact_name: '',
        emergency_contact_phone: '',
        emergency_contact_relationship: '',
        notes: '',
        tags: [],
        custom_fields: null,
        is_active: true
      });
      loadData();
    } catch (error) {
      console.error('Failed to save staff:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert('Failed to save staff member');
    }
  };

  const handleEdit = (staffMember) => {
    setEditingStaff(staffMember);
    setFormData({
      business_id: staffMember.business_id || '',
      employee_id: staffMember.employee_id || '',
      first_name: staffMember.first_name || '',
      last_name: staffMember.last_name || '',
      email: staffMember.email || '',
      phone: staffMember.phone || '',
      date_of_birth: staffMember.date_of_birth ? staffMember.date_of_birth.split('T')[0] : '',
      ssn_last_4: staffMember.ssn_last_4 || '',
      street_address: staffMember.street_address || '',
      city: staffMember.city || '',
      state: staffMember.state || '',
      zip_code: staffMember.zip_code || '',
      country: staffMember.country || 'USA',
      hire_date: staffMember.hire_date ? staffMember.hire_date.split('T')[0] : '',
      termination_date: staffMember.termination_date ? staffMember.termination_date.split('T')[0] : '',
      employment_status: staffMember.employment_status || 'active',
      employment_type: staffMember.employment_type || 'full_time',
      job_title: staffMember.job_title || '',
      department: staffMember.department || '',
      reports_to: staffMember.reports_to || '',
      pay_type: staffMember.pay_type || 'hourly',
      pay_rate: staffMember.pay_rate || '',
      pay_frequency: staffMember.pay_frequency || 'biweekly',
      overtime_eligible: staffMember.overtime_eligible !== false,
      overtime_rate: staffMember.overtime_rate || '1.5',
      health_insurance: staffMember.health_insurance || false,
      dental_insurance: staffMember.dental_insurance || false,
      vision_insurance: staffMember.vision_insurance || false,
      retirement_401k: staffMember.retirement_401k || false,
      retirement_contribution_percent: staffMember.retirement_contribution_percent || '',
      pto_hours_per_year: staffMember.pto_hours_per_year || '',
      sick_hours_per_year: staffMember.sick_hours_per_year || '',
      tax_filing_status: staffMember.tax_filing_status || '',
      federal_withholding_allowances: staffMember.federal_withholding_allowances || '0',
      state_withholding_allowances: staffMember.state_withholding_allowances || '0',
      additional_withholding: staffMember.additional_withholding || '0',
      emergency_contact_name: staffMember.emergency_contact_name || '',
      emergency_contact_phone: staffMember.emergency_contact_phone || '',
      emergency_contact_relationship: staffMember.emergency_contact_relationship || '',
      notes: staffMember.notes || '',
      tags: staffMember.tags || [],
      custom_fields: staffMember.custom_fields || null,
      is_active: staffMember.is_active !== false
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this staff member?')) return;
    
    try {
      await staffAPI.delete(id);
      loadData();
    } catch (error) {
      console.error('Failed to delete staff:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert('Failed to delete staff member');
    }
  };

  if (loading) {
    return <div className="p-6">Loading staff data...</div>;
  }

  return (
    <ErrorBoundary section="Staff Management">
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Staff Management</h1>
            <p className="text-gray-600 mt-1">Manage your team members and employee information</p>
          </div>
          <button
            onClick={() => {
              setEditingStaff(null);
              setFormData({
                business_id: '',
                employee_id: '',
                first_name: '',
                last_name: '',
                email: '',
                phone: '',
                date_of_birth: '',
                ssn_last_4: '',
                street_address: '',
                city: '',
                state: '',
                zip_code: '',
                country: 'USA',
                hire_date: todayLocal(),
                termination_date: '',
                employment_status: 'active',
                employment_type: 'full_time',
                job_title: '',
                department: '',
                reports_to: '',
                pay_type: 'hourly',
                pay_rate: '',
                pay_frequency: 'biweekly',
                overtime_eligible: true,
                overtime_rate: '1.5',
                health_insurance: false,
                dental_insurance: false,
                vision_insurance: false,
                retirement_401k: false,
                retirement_contribution_percent: '',
                pto_hours_per_year: '',
                sick_hours_per_year: '',
                tax_filing_status: '',
                federal_withholding_allowances: '0',
                state_withholding_allowances: '0',
                additional_withholding: '0',
                emergency_contact_name: '',
                emergency_contact_phone: '',
                emergency_contact_relationship: '',
                notes: '',
                tags: [],
                custom_fields: null,
                is_active: true
              });
              setShowModal(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            + Add Staff Member
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Total Staff</div>
              <div className="text-2xl font-bold text-gray-800">{stats.total_staff || 0}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Active</div>
              <div className="text-2xl font-bold text-green-600">{stats.active_staff || 0}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Avg Hourly Rate</div>
              <div className="text-2xl font-bold text-blue-600">
                ${parseFloat(stats.avg_hourly_rate || 0).toFixed(2)}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Avg Annual Salary</div>
              <div className="text-2xl font-bold text-purple-600">
                ${parseFloat(stats.avg_annual_salary || 0).toLocaleString()}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <select
              value={filters.employment_status}
              onChange={(e) => setFilters({ ...filters, employment_status: e.target.value })}
              className="border border-gray-300 rounded px-3 py-2"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="on_leave">On Leave</option>
              <option value="terminated">Terminated</option>
            </select>

            <select
              value={filters.employment_type}
              onChange={(e) => setFilters({ ...filters, employment_type: e.target.value })}
              className="border border-gray-300 rounded px-3 py-2"
            >
              <option value="">All Types</option>
              <option value="full_time">Full Time</option>
              <option value="part_time">Part Time</option>
              <option value="contract">Contract</option>
              <option value="temporary">Temporary</option>
            </select>

            <input
              type="text"
              placeholder="Department"
              value={filters.department}
              onChange={(e) => setFilters({ ...filters, department: e.target.value })}
              className="border border-gray-300 rounded px-3 py-2"
            />
          </div>
        </div>
      </div>

      {/* Staff Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job Title</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pay</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {staff.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                  No staff members found. Add your first employee to get started.
                </td>
              </tr>
            ) : (
              staff.map((member) => (
                <tr key={member.staff_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">
                      {member.first_name} {member.last_name}
                    </div>
                    <div className="text-sm text-gray-500">{member.email}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">{member.job_title || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{member.department || '-'}</td>
                  <td className="px-6 py-4">
                    <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                      {member.employment_type?.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {member.pay_type === 'hourly' ? `$${member.pay_rate}/hr` : `$${parseFloat(member.pay_rate || 0).toLocaleString()}/yr`}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      member.employment_status === 'active' ? 'bg-green-100 text-green-800' :
                      member.employment_status === 'on_leave' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {member.employment_status?.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <button
                      onClick={() => handleEdit(member)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(member.staff_id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[95vh] overflow-y-auto my-8">
            <h2 className="text-2xl font-bold mb-4">
              {editingStaff ? 'Edit Staff Member' : 'Add Staff Member'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Basic Information */}
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input type="text" required value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input type="text" required value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input type="email" required value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input type="tel" value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee ID</label>
                  <input type="text" value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                  <input type="date" value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SSN Last 4</label>
                  <input type="text" maxLength="4" pattern="[0-9]{4}" value={formData.ssn_last_4}
                    onChange={(e) => setFormData({ ...formData, ssn_last_4: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" placeholder="1234" />
                </div>
              </div>

              {/* Address */}
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 mt-4">Address</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                <input type="text" value={formData.street_address}
                  onChange={(e) => setFormData({ ...formData, street_address: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2" />
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input type="text" value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input type="text" value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Zip Code</label>
                  <input type="text" value={formData.zip_code}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <input type="text" value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
              </div>

              {/* Employment */}
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 mt-4">Employment Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Job Title *</label>
                  <input type="text" required value={formData.job_title}
                    onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  <input type="text" value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type</label>
                  <select value={formData.employment_type}
                    onChange={(e) => setFormData({ ...formData, employment_type: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2">
                    <option value="full_time">Full Time</option>
                    <option value="part_time">Part Time</option>
                    <option value="contract">Contract</option>
                    <option value="temporary">Temporary</option>
                    <option value="intern">Intern</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employment Status</label>
                  <select value={formData.employment_status}
                    onChange={(e) => setFormData({ ...formData, employment_status: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2">
                    <option value="active">Active</option>
                    <option value="on_leave">On Leave</option>
                    <option value="terminated">Terminated</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hire Date *</label>
                  <input type="date" required value={formData.hire_date}
                    onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Termination Date</label>
                  <input type="date" value={formData.termination_date}
                    onChange={(e) => setFormData({ ...formData, termination_date: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reports To</label>
                  <select value={formData.reports_to}
                    onChange={(e) => setFormData({ ...formData, reports_to: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2">
                    <option value="">None</option>
                    {staff.filter(s => s.staff_id !== editingStaff?.staff_id).map(s => (
                      <option key={s.staff_id} value={s.staff_id}>{s.first_name} {s.last_name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Compensation */}
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 mt-4">Compensation</h3>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pay Type</label>
                  <select value={formData.pay_type}
                    onChange={(e) => setFormData({ ...formData, pay_type: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2">
                    <option value="hourly">Hourly</option>
                    <option value="salary">Salary</option>
                    <option value="commission">Commission</option>
                    <option value="contract">Contract</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pay Rate *</label>
                  <input type="number" step="0.01" required value={formData.pay_rate}
                    onChange={(e) => setFormData({ ...formData, pay_rate: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    placeholder={formData.pay_type === 'hourly' ? '$/hour' : '$/year'} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pay Frequency</label>
                  <select value={formData.pay_frequency}
                    onChange={(e) => setFormData({ ...formData, pay_frequency: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2">
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Biweekly</option>
                    <option value="semimonthly">Semi-Monthly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                <div>
                  <label className="flex items-center mt-7">
                    <input type="checkbox" checked={formData.overtime_eligible}
                      onChange={(e) => setFormData({ ...formData, overtime_eligible: e.target.checked })}
                      className="mr-2" />
                    <span className="text-sm font-medium text-gray-700">Overtime Eligible</span>
                  </label>
                </div>
              </div>

              {formData.overtime_eligible && (
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Overtime Rate (multiplier)</label>
                    <input type="number" step="0.1" value={formData.overtime_rate}
                      onChange={(e) => setFormData({ ...formData, overtime_rate: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2" placeholder="1.5" />
                  </div>
                </div>
              )}

              {/* Benefits */}
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 mt-4">Benefits</h3>
              <div className="grid grid-cols-4 gap-4">
                <label className="flex items-center">
                  <input type="checkbox" checked={formData.health_insurance}
                    onChange={(e) => setFormData({ ...formData, health_insurance: e.target.checked })}
                    className="mr-2" />
                  <span className="text-sm font-medium text-gray-700">Health Insurance</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" checked={formData.dental_insurance}
                    onChange={(e) => setFormData({ ...formData, dental_insurance: e.target.checked })}
                    className="mr-2" />
                  <span className="text-sm font-medium text-gray-700">Dental Insurance</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" checked={formData.vision_insurance}
                    onChange={(e) => setFormData({ ...formData, vision_insurance: e.target.checked })}
                    className="mr-2" />
                  <span className="text-sm font-medium text-gray-700">Vision Insurance</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" checked={formData.retirement_401k}
                    onChange={(e) => setFormData({ ...formData, retirement_401k: e.target.checked })}
                    className="mr-2" />
                  <span className="text-sm font-medium text-gray-700">401k</span>
                </label>
              </div>

              {formData.retirement_401k && (
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">401k Contribution %</label>
                    <input type="number" step="0.1" value={formData.retirement_contribution_percent}
                      onChange={(e) => setFormData({ ...formData, retirement_contribution_percent: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2" placeholder="5.0" />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PTO Hours/Year</label>
                  <input type="number" step="0.1" value={formData.pto_hours_per_year}
                    onChange={(e) => setFormData({ ...formData, pto_hours_per_year: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" placeholder="80" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sick Hours/Year</label>
                  <input type="number" step="0.1" value={formData.sick_hours_per_year}
                    onChange={(e) => setFormData({ ...formData, sick_hours_per_year: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" placeholder="40" />
                </div>
              </div>

              {/* Tax Information */}
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 mt-4">Tax Withholding</h3>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Filing Status</label>
                  <select value={formData.tax_filing_status}
                    onChange={(e) => setFormData({ ...formData, tax_filing_status: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2">
                    <option value="">Select...</option>
                    <option value="single">Single</option>
                    <option value="married">Married</option>
                    <option value="married_separate">Married Filing Separately</option>
                    <option value="head_of_household">Head of Household</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Federal Allowances</label>
                  <input type="number" value={formData.federal_withholding_allowances}
                    onChange={(e) => setFormData({ ...formData, federal_withholding_allowances: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State Allowances</label>
                  <input type="number" value={formData.state_withholding_allowances}
                    onChange={(e) => setFormData({ ...formData, state_withholding_allowances: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Additional Withholding $</label>
                  <input type="number" step="0.01" value={formData.additional_withholding}
                    onChange={(e) => setFormData({ ...formData, additional_withholding: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
              </div>

              {/* Emergency Contact */}
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 mt-4">Emergency Contact</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                  <input type="text" value={formData.emergency_contact_name}
                    onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
                  <input type="tel" value={formData.emergency_contact_phone}
                    onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                  <input type="text" value={formData.emergency_contact_relationship}
                    onChange={(e) => setFormData({ ...formData, emergency_contact_relationship: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" placeholder="Spouse, Parent, etc." />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="3" className="w-full border border-gray-300 rounded px-3 py-2"></textarea>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingStaff(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingStaff ? 'Update' : 'Create'} Staff Member
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </ErrorBoundary>
  );
};

export default Staff;
